import { CloseOutlined, PlusOutlined } from '@ant-design/icons'
import { Alert, Button, Card, Col, Divider, Row, Skeleton, Space, Typography } from 'antd'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Markdown from 'react-markdown'

import { AccountPublic, AccountType, BillingProduct, BillingSubscription, PlanType, Subscription } from '@pollination-solutions/pollination-sdk'

import { SubscriptionStatusProvider, useAccountSubscriptions, useBillingAPI, useSubscriptionStatus } from 'hooks'
import { ResourceType, useRoles } from 'hooks/useRoles'
import { BillingForm, SubscriptionCard } from 'molecules'
import { BillingSubscriptionCard } from 'molecules/BillingSubscriptionCard'
import { UsageSummaryDashboard } from 'organisms'
import { isInvoiceSubscription, isNewBillingSubscription } from 'utils/subscriptionUtils'

enum PlanGroup {
  Cloud = 'cloud',
  Seat = 'seat',
  Plugin = 'plugin',
}

interface SubscriptionProps {
  account: AccountPublic
}

interface ProductType {
  group: PlanGroup
  type: PlanType
  feature?: string
}

const ACTIVE_PRODUCTS: ProductType[] = [
  // {
  //   group: PlanGroup.Cloud,
  //   type: PlanType.Cloud,
  // },
  {
    group: PlanGroup.Seat,
    type: PlanType.Seat,
  },
  {
    group: PlanGroup.Cloud,
    type: PlanType.CloudCompute,
  },
  {
    group: PlanGroup.Plugin,
    type: PlanType.RhinoPlugin,
  },
  {
    group: PlanGroup.Plugin,
    type: PlanType.RevitPlugin,
  },
  {
    group: PlanGroup.Plugin,
    type: PlanType.BundledPlugin,
  },
]

interface OldBillingGroupProps {
  subscribed: ProductType[]
  notSubscribed: ProductType[]
  account: AccountPublic
  allowNewSubscriptions: boolean
  legacySubscriptions: Subscription[]
}
const OldBillingGroup = ({ subscribed, notSubscribed, account, allowNewSubscriptions, legacySubscriptions }: OldBillingGroupProps) => {
  const { getForPlanType } = useAccountSubscriptions(account.name)

  // Filter to only show products that have legacy subscriptions or allow new subscriptions
  const filterForLegacyOnly = (products: ProductType[]) => {
    return products.filter(product => {
      const res = getForPlanType(product.type, account.account_type as AccountType)
      // Show if there's a legacy subscription for this product type
      if (res.subscription) {
        const hasLegacySubscription = legacySubscriptions.some(sub => sub.id === res.subscription?.id)
        return hasLegacySubscription
      }
      // Or show if new subscriptions are allowed
      return allowNewSubscriptions
    })
  }

  const filteredSubscribed = filterForLegacyOnly(subscribed)
  const filteredNotSubscribed = allowNewSubscriptions ? notSubscribed : []

  return (
    <Row gutter={[24, 24]}>
      {filteredSubscribed.map((product, index) => {
        const res = getForPlanType(product.type, account.account_type as AccountType)
        return res.plans.length !== 0 && (
          <Col span={24} key={`old-subscribed-${index}`}>
            <SubscriptionCard {...res} allowNewSubscriptions={allowNewSubscriptions} />
          </Col>
        )
      })}
      {filteredNotSubscribed.map((product, index) => {
        const res = getForPlanType(product.type, account.account_type as AccountType)
        return res.plans.length !== 0 && (
          <Col span={24} key={`old-not-subscribed-${index}`}>
            <SubscriptionCard {...res} allowNewSubscriptions={allowNewSubscriptions} />
          </Col>
        )
      })}
    </Row>
  )
}

interface NewBillingGroupProps {
  billingProducts: BillingProduct[]
  billingSubscriptions: BillingSubscription[]
  account: AccountPublic
  onSubscriptionUpdate?: () => void
}
const NewBillingGroup = ({ 
  billingProducts, 
  billingSubscriptions, 
  account, 
  onSubscriptionUpdate
}: NewBillingGroupProps) => {
  const [showCreateForm, setShowCreateForm] = useState(false)
  const { status } = useSubscriptionStatus()

  return (
    <Row gutter={[24, 24]}>
      {/* Existing subscriptions */}
      {billingSubscriptions.map(subscription => (
        <Col span={24} key={subscription.id}>
          <BillingSubscriptionCard
            products={billingProducts}
            subscription={subscription}
            existingSubscriptions={billingSubscriptions}
            onUpdateSuccess={onSubscriptionUpdate}
          />
        </Col>
      ))}

      {/* Create new subscription button */}
      {!showCreateForm
        /* Only show if there is no active subscription */
        && (!status.yearly || !status.monthly)
        && (
          <Col span={24}>
            <Card
              style={{
                border: '2px dashed #52c41a',
                textAlign: 'center',
                backgroundColor: '#f6ffed',
                cursor: 'pointer'
              }}
              onClick={() => setShowCreateForm(true)}
              hoverable
            >
              <Space direction="vertical" size="small">
                <PlusOutlined style={{ fontSize: '24px', color: '#52c41a' }} />
                <Typography.Text strong style={{ color: '#52c41a' }}>
                  {status.monthly && !status.yearly
                    ? 'Create Yearly Subscription'
                    : status.yearly && !status.monthly
                      ? 'Create Monthly Subscription'
                      : 'Create New Subscription'}
                </Typography.Text>
              </Space>
            </Card>
          </Col>
        )}

      {/* New subscription form */}
      {showCreateForm && (
        <Col span={24}>
          <Card
            title="Create New Subscription"
            extra={
              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={() => setShowCreateForm(false)}
                size="small"
              />
            }
            style={{ border: '2px solid #52c41a' }}
          >
            <BillingForm
              billingProducts={billingProducts}
              account={account}
              existingSubscriptions={billingSubscriptions}
              onSuccess={() => {
                setShowCreateForm(false)
                if (onSubscriptionUpdate) {
                  onSubscriptionUpdate()
                }
              }} />
          </Card>
        </Col>
      )}
    </Row>
  )
}

const Subscription = ({ account }: SubscriptionProps): React.ReactElement => {
  const { loading, getForPlanType, subscriptions } = useAccountSubscriptions(account.name)
  const { canAdmin, canRead } = useRoles()
  const { listBillingProducts, listBillingSubscriptions } = useBillingAPI()

  const [billingProducts, setBillingProducts] = useState<BillingProduct[]>([])
  const [billingSubscriptions, setBillingSubscriptions] = useState<BillingSubscription[]>([])
  const [billingLoading, setBillingLoading] = useState(false)

  const isOrgAccount = useMemo(() => account.account_type === AccountType.Org, [account])


  const refreshBillingData = useCallback(async () => {
    setBillingLoading(true)
    try {
      const [products, subscriptions] = await Promise.all([
        listBillingProducts(),
        listBillingSubscriptions(account.name)
      ])
      setBillingProducts(products)
      setBillingSubscriptions(subscriptions)
    } catch (error) {
      console.error('Failed to refresh billing data:', error)
    } finally {
      setBillingLoading(false)
    }
  }, [listBillingProducts, listBillingSubscriptions, account.name])

  useEffect(() => {
    refreshBillingData()
  }, [refreshBillingData])

  const legacySubscriptions = useMemo(() => {
    // Filter out new Billing subscriptions
    // If we are also using the new Billing API then filter out the ones without an external ID (ie: those that are not paid)
    return subscriptions?.filter((sub) => !isNewBillingSubscription(sub)) ?? []
  }, [subscriptions])

  const invoiceSubscriptions = useMemo(() => {
    return subscriptions?.filter((sub) => isInvoiceSubscription(sub)) ?? []
  }, [subscriptions])

  const {
    hasLegacySeatSubs,
    hasLegacyCloudSubs,
    hasLegacyPluginSubs,
  }  = useMemo(() => {
    if (!legacySubscriptions) return {
      hasLegacySeatSubs: false,
      hasLegacyCloudSubs: false,
      hasLegacyPluginSubs: false,
    }
    return {
      hasLegacySeatSubs: legacySubscriptions.filter((sub) => PlanType.Seat === sub.type).length > 0,
      hasLegacyCloudSubs: legacySubscriptions.filter((sub) => {
        return sub.plan_slug === 'professional-tier'
        || PlanType.CloudCompute === sub.type
      }).length > 0,
      hasLegacyPluginSubs: legacySubscriptions.filter((sub) => [PlanType.BundledPlugin, PlanType.RevitPlugin, PlanType.RhinoPlugin].includes(sub.type as any)).length > 0,
    }
  }, [legacySubscriptions])

  const { 
    hasInvoiceSeatSubs,
    hasInvoicePluginSubs,
  } = useMemo(() => {
    if (!invoiceSubscriptions) return {
      hasInvoiceSeatSubs: false,
      hasInvoicePluginSubs: false,
    }
    return {
      hasInvoiceSeatSubs: invoiceSubscriptions.filter((sub) => PlanType.Seat === sub.type).length > 0,
      hasInvoicePluginSubs: invoiceSubscriptions.filter((sub) => [PlanType.BundledPlugin, PlanType.RevitPlugin, PlanType.RhinoPlugin].includes(sub.type as any)).length > 0,
    }
  }, [invoiceSubscriptions])

  const { cloudSubscribed, cloudNotSubscribed } = useMemo(() => {
    // @ts-ignore
    return ACTIVE_PRODUCTS.reduce((p, product) => {
      if (product.group != PlanGroup.Cloud) {
        return p
      }
      const { subscription } = getForPlanType(product.type, account.account_type as AccountType)
      if (subscription) {
        p.cloudSubscribed.push(product)
      } else {
        p.cloudNotSubscribed.push(product)
      }
      return p
    }, { cloudSubscribed: [] as any[], cloudNotSubscribed: [] as any[] })
  }, [account.account_type, getForPlanType])

  const { seatSubscribed, seatNotSubscribed } = useMemo(() => {
    // @ts-ignore
    return ACTIVE_PRODUCTS.reduce((p, product) => {
      if (product.group != PlanGroup.Seat) {
        return p
      }
      const { subscription } = getForPlanType(product.type, account.account_type as AccountType)
      if (subscription) {
        p.seatSubscribed.push(product)
      } else {
        p.seatNotSubscribed.push(product)
      }
      return p
    }, { seatSubscribed: [] as any[], seatNotSubscribed: [] as any[] })
  }, [account.account_type, getForPlanType])

  const { pluginSubscribed, pluginNotSubscribed } = useMemo(() => {
    // @ts-ignore
    return ACTIVE_PRODUCTS.reduce((p, product) => {
      if (product.group != PlanGroup.Plugin) {
        return p
      }
      const { subscription } = getForPlanType(product.type, account.account_type as AccountType)
      if (subscription) {
        p.pluginSubscribed.push(product)
      } else {
        p.pluginNotSubscribed.push(product)
      }
      return p
    }, { pluginSubscribed: [] as any[], pluginNotSubscribed: [] as any[] })
  }, [account.account_type, getForPlanType])

  const subscriptionsMovedToOrganizationsNotice = `
  We have streamlined how teams access Pollination products. **Subscriptions are now managed at the organization level** to better support collaborative work.

  #### What This Means for You

  - Individual account subscriptions are no longer available for new purchases.
  - All new subscriptions must be created within an organization.
  - Existing individual subscriptions will continue to work.

  #### Getting Started

  To purchase a subscription:

  1. **Create or join an organization** by clicking right [here](/${account.name}/create-org) to open a new window.
  2. Navigate to the **Subscription tab** within your organization
  3. Select your products (Rhino Plugin, Revit Plugin, or Additional Seats)
  4. Complete your purchase via credit card or request an invoice

  #### Why Organizations?

  Organizations make it easier to:
  - Manage concurrent activations and seats for your team
  - Add or remove team members without disrupting subscriptions
  - Track usage and billing in one centralized location
  - Scale your subscription as your team grows

  #### Need Help?

  If you have questions about migrating to an organization-based subscription, please contact us at **sales@pollination.solutions**
  `

  if (loading) return (
    <Skeleton active loading={loading} paragraph />
  )

  return (
    <SubscriptionStatusProvider
      existingSubscriptions={billingSubscriptions}>
      {canAdmin(ResourceType.Subscription) && (
        <>
          {isOrgAccount && (
            <>
              <Divider orientation='left'>
                Subscriptions
                {billingSubscriptions.length > 0 && (
                  <Typography.Text type="secondary" style={{ fontSize: '12px', marginLeft: 8 }}>
                    ({billingSubscriptions.length} active)
                  </Typography.Text>
                )}
              </Divider>
              {billingLoading ? (
                <Skeleton active loading={true} paragraph />
              ) : 
              (
                <NewBillingGroup
                  billingProducts={billingProducts}
                  billingSubscriptions={billingSubscriptions}
                  account={account}
                  onSubscriptionUpdate={refreshBillingData}
                />
              )}
            </>
          )}

          {!isOrgAccount &&
            <Alert
              message="Subscriptions Have Moved to Organizations"
              // description="Additional description and information about copywriting."
              type="info"
              showIcon
              description={
                <Markdown>
                  {subscriptionsMovedToOrganizationsNotice}
                </Markdown>
              }
            />
          }

          {hasInvoiceSeatSubs &&
          <>
            <Divider orientation='left'>
              Seat Subscriptions
            </Divider>
            <OldBillingGroup
              subscribed={seatSubscribed}
              notSubscribed={seatNotSubscribed}
              account={account}
              allowNewSubscriptions={false}
              legacySubscriptions={invoiceSubscriptions}
            />
          </>}
          {hasInvoicePluginSubs &&
          <>
            <Divider orientation='left'>
              Plugin Subscriptions
            </Divider>
            <OldBillingGroup
              subscribed={pluginSubscribed}
              notSubscribed={pluginNotSubscribed}
              account={account}
              allowNewSubscriptions={false}
              legacySubscriptions={invoiceSubscriptions}
            />
          </>}

          {hasLegacySeatSubs &&
          <>
            <Divider orientation='left'>
              Other Seat Subscriptions
            </Divider>
            <OldBillingGroup
              subscribed={seatSubscribed}
              notSubscribed={seatNotSubscribed}
              account={account}
              allowNewSubscriptions={false}
              legacySubscriptions={legacySubscriptions}
            />
          </>}
          {hasLegacyCloudSubs &&
          <>
            <Divider orientation='left'>
              Other Cloud Subscriptions
            </Divider>
            <OldBillingGroup
              subscribed={cloudSubscribed}
              notSubscribed={cloudNotSubscribed}
              account={account}
              allowNewSubscriptions={false}
              legacySubscriptions={legacySubscriptions}
            />
          </>}
          {hasLegacyPluginSubs &&
          <>
            <Divider orientation='left'>
              Other Plugin Subscriptions
            </Divider>
            <OldBillingGroup
              subscribed={pluginSubscribed}
              notSubscribed={pluginNotSubscribed}
              account={account}
              allowNewSubscriptions={false}
              legacySubscriptions={legacySubscriptions}
            />
          </>}
        </>
      )}

      {canRead(ResourceType.Quota) && legacySubscriptions.length > 0 && isOrgAccount &&(
        <UsageSummaryDashboard account={account} hasCloudSubs={hasLegacyCloudSubs} />
      )}
    </SubscriptionStatusProvider>
  )
}

export default Subscription
